import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from '@emotion/styled';

type Props = {
  children: string;
  className?: string;
};

/**
 * AiChatView AI 답변 마크다운 렌더러.
 * 기준 원본: components/Record/TalkFLow/Markdown.tsx (에디터 테마와 동일한 구조 값).
 * 색상만 CSS 변수(--aichat-md-*)로 토큰화해 라이트/다크 두 테마에 대응한다.
 * - 폰트 크기/기본 색은 부모(AiText)에서 상속받는다.
 */
const Markdown = ({ children, className }: Props) => (
  <Wrap className={className}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ node: _node, ...props }) => (
          <a {...props} target="_blank" rel="noopener noreferrer" />
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  </Wrap>
);

export default Markdown;

const Wrap = styled.div`
  font: inherit;
  color: inherit;
  overflow-wrap: break-word;
  word-break: break-word;

  > *:last-child {
    margin-bottom: 0;
  }

  p {
    margin: 0 0 16px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0 0 16px;
    font-weight: 600;
    line-height: 1.25;
  }
  * + h1,
  * + h2,
  * + h3,
  * + h4,
  * + h5,
  * + h6 {
    margin-top: 24px;
  }
  h1 {
    font-size: 2em;
  }
  h2 {
    font-size: 1.5em;
  }
  h3 {
    font-size: 1.25em;
  }
  h4 {
    font-size: 1em;
  }
  h5 {
    font-size: 0.875em;
  }
  h6 {
    font-size: 0.85em;
  }

  ul,
  ol {
    margin: 0 0 16px;
    padding-left: 2em;
  }
  li + li {
    margin-top: 0.25em;
  }
  li > ul,
  li > ol {
    margin: 0.25em 0 0;
  }
  li.task-list-item {
    list-style: none;
  }
  li.task-list-item input[type='checkbox'] {
    margin-right: 8px;
  }

  strong {
    font-weight: 600;
  }
  em {
    font-style: italic;
  }

  a {
    color: var(--aichat-md-link);
    text-decoration: underline;
  }

  blockquote {
    margin: 0 0 16px;
    padding: 0 1em;
    border-left: 0.25em solid var(--aichat-md-quote-border);
    color: var(--aichat-md-quote-text);
  }

  hr {
    height: 1px;
    margin: 0 0 24px;
    padding: 0;
    border: 0;
    background-color: var(--aichat-md-hr);
  }
  * + hr {
    margin-top: 24px;
  }

  code {
    padding: 0.2em 0.4em;
    background: var(--aichat-md-code-bg);
    border-radius: 6px;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 0.85em;
  }
  pre {
    margin: 0 0 16px;
    padding: 16px;
    background: var(--aichat-md-pre-bg);
    border-radius: 6px;
    line-height: 1.45;
    font-size: 0.85em;
    overflow-x: auto;
  }
  pre code {
    padding: 0;
    background: none;
    font-size: 1em;
  }

  table {
    display: block;
    width: max-content;
    max-width: 100%;
    margin: 0 0 16px;
    border-collapse: collapse;
    overflow-x: auto;
  }
  th,
  td {
    padding: 6px 13px;
    border: 1px solid var(--aichat-md-table-border);
    text-align: left;
  }
  th {
    background: var(--aichat-md-th-bg);
    font-weight: 600;
  }
  th > :last-child,
  td > :last-child {
    margin-bottom: 0;
  }
`;
